/** localStorage key for optional home/landing accent override (dev/preview tool in header). */
export const LANDING_ACCENT_STORAGE_KEY = 'landing_accent_override';

/** Desk-LED accent used on marketing surfaces (Precision Desk / HomeDesk). */
export const DESK_LED_ACCENT = '#5B6CFF';

/** Default landing accent — desk-LED (marketing). App shell primary remains themes.js purple. */
export const DEFAULT_LANDING_ACCENT = DESK_LED_ACCENT;

/** Quick preset: purple accent (rgb 103, 64, 212). */
export const LANDING_ACCENT_PRESET_PURPLE = '#6740d4';

const ACCENT_CSS_PROPS = [
  '--landing-accent',
  '--landing-accent-rgb',
  '--landing-accent-light-rgb',
  '--landing-accent-lighter-rgb',
  '--landing-accent-dark-rgb',
  '--landing-accent-deep-rgb',
  '--landing-accent-highlight-rgb',
];

export function normalizeHex(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim().toLowerCase();
  if (!h.startsWith('#')) h = `#${h}`;
  if (/^#[0-9a-f]{3}$/.test(h)) {
    h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  return /^#[0-9a-f]{6}$/.test(h) ? h : null;
}

export function hexToRgb(hex) {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const n = parseInt(normalized.slice(1), 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

export function rgbToTuple({ r, g, b }) {
  return `${r}, ${g}, ${b}`;
}

/** Blend accent toward a target RGB (weight 0–1 = fraction of target). */
export function mixAccentRgb(hex, target, weight) {
  const base = hexToRgb(hex);
  if (!base) return null;
  const w = Math.min(1, Math.max(0, weight));
  const mix = (a, b) => Math.round(a * (1 - w) + b * w);
  return rgbToTuple({
    r: mix(base.r, target.r),
    g: mix(base.g, target.g),
    b: mix(base.b, target.b),
  });
}

export function getStoredLandingAccent() {
  try {
    const stored = localStorage.getItem(LANDING_ACCENT_STORAGE_KEY);
    return normalizeHex(stored) || null;
  } catch {
    return null;
  }
}

export function findLandingPageRoot(fromNode) {
  if (fromNode?.closest) return fromNode.closest('.landing-page');
  return document.querySelector('.landing-page');
}

/**
 * Apply a live accent override on `.landing-page` (inline CSS vars beat stylesheet defaults).
 * Derivatives (dark/deep/highlight RGB tuples) are computed from the picked hex.
 */
export function applyLandingAccentOverride(root, hex) {
  const el = root || document.querySelector('.landing-page');
  const normalized = normalizeHex(hex);
  if (!el || !normalized) return null;

  const rgb = hexToRgb(normalized);
  if (!rgb) return null;

  el.style.setProperty('--landing-accent', normalized);
  el.style.setProperty('--landing-accent-rgb', rgbToTuple(rgb));
  el.style.setProperty('--landing-accent-light-rgb', rgbToTuple(rgb));
  el.style.setProperty('--landing-accent-lighter-rgb', rgbToTuple(rgb));
  el.style.setProperty('--landing-accent-dark-rgb', mixAccentRgb(normalized, { r: 0, g: 0, b: 0 }, 0.28));
  el.style.setProperty('--landing-accent-deep-rgb', mixAccentRgb(normalized, { r: 0, g: 0, b: 0 }, 0.52));
  el.style.setProperty('--landing-accent-highlight-rgb', mixAccentRgb(normalized, { r: 255, g: 255, b: 255 }, 0.55));

  try {
    localStorage.setItem(LANDING_ACCENT_STORAGE_KEY, normalized);
  } catch {
    /* ignore quota / private mode */
  }

  return normalized;
}

/** Remove inline override so Landing.css defaults apply again. */
export function clearLandingAccentOverride(root) {
  const el = root || document.querySelector('.landing-page');
  if (!el) return;

  ACCENT_CSS_PROPS.forEach((prop) => el.style.removeProperty(prop));

  try {
    localStorage.removeItem(LANDING_ACCENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Restore saved accent when landing page mounts (no-op if none saved). */
export function restoreLandingAccentOverride(root) {
  const stored = getStoredLandingAccent();
  if (stored) applyLandingAccentOverride(root, stored);
}
